
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";

console.log("ProfileButton.tsx - Loading with completely custom implementation");

const ProfileButtonContent = () => {
  console.log("ProfileButtonContent - Starting render");
  
  try {
    const { user, signOut, isAuthenticated } = useAuth();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    console.log("ProfileButton - Auth state:", { isAuthenticated, user: user?.email });
    
    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      };

      if (isDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isDropdownOpen]);
    
    if (!isAuthenticated) {
      console.log("ProfileButton - Rendering sign in link");
      return (
        <Link 
          to="/auth" 
          className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-primary text-primary-foreground hover:bg-primary/90 h-10 py-2 px-4"
        >
          Sign In
        </Link>
      );
    }

    const getInitials = () => {
      return user?.email?.substring(0, 2).toUpperCase() || "??";
    };

    const handleSignOut = () => {
      console.log("ProfileButton - Sign out clicked");
      signOut();
      setIsDropdownOpen(false);
    };

    const toggleDropdown = () => {
      console.log("ProfileButton - Toggle dropdown clicked, current state:", isDropdownOpen);
      setIsDropdownOpen(!isDropdownOpen);
    };

    console.log("ProfileButton - Rendering authenticated user dropdown");

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-md p-2 border border-gray-200 bg-white transition-colors"
          onClick={toggleDropdown}
        >
          <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium">
            {getInitials()}
          </div>
          <span className="text-sm font-medium">{user?.email}</span>
          <svg 
            className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isDropdownOpen && (
          <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 shadow-lg rounded-md z-[9999] py-1">
            <button
              type="button"
              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm transition-colors border-none bg-transparent cursor-pointer"
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error("ProfileButtonContent - Error during render:", error);
    return (
      <div className="px-4 py-2 text-sm text-red-600">
        Profile Error: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }
};

const ProfileButton = () => {
  console.log("ProfileButton wrapper - Rendering with error boundary");
  
  return (
    <ErrorBoundary fallback={
      <div className="px-4 py-2 text-sm text-red-600">
        Profile Component Error - Fallback Active
      </div>
    }>
      <ProfileButtonContent />
    </ErrorBoundary>
  );
};

console.log("ProfileButton.tsx - Component exported successfully");

export default ProfileButton;
